import $ from 'jquery'
import _ from 'lodash'
import KDevice from '../../core/utils/KDevice'
import KPopper, { PopperType } from '../../core/mixins/popper/KPopper'
import ResizeAware from '../resize-aware'
import { getFirstComponentChild } from '../../core/utils/KVdom'
import { getCorrectEventName } from '../../core/utils'
import raf from 'raf'
import { window } from 'ssr-window'

const transitionEnd = getCorrectEventName(window, 'transitionend')

export default {
  name: 'KPopover',
  mixins: [KPopper],
  props: {
    title: {
      type: String,
      default: '',
    },
    trigger: {
      // Override KPopper's trigger
      type: String,
      default: 'click',
      validator: (value) => ['click', 'focus', 'hover', 'manual'].indexOf(value) !== -1,
    },
    adjustOnChange: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      popperType: PopperType.POPOVER,
      references: [],
      popperWidth: '',
    }
  },
  computed: {
    popoverCss() {
      const css = {
        'km-popover': true,
        'is-hidden': !this.activated,
        ...this.popperClassObjectLike,
      }

      if (this.size) {
        css[`is-${this.size}`] = true
      }
      if (!this.arrow) {
        css[`km-popover--no-arrow`] = true
      }

      return css
    },
  },
  watch: {
    value(value) {
      if (!value) {
        return
      }
      if (this.widthEqualToReference && this.$el) {
        this.popperWidth = this.$el.getBoundingClientRect().width
      }
    },
    references(references) {
      _.forEach(references, (reference) => {
        this.registerReference(reference)
      })
    },
  },
  mounted() {
    this.reference = this.reference || this.$refs.reference || this.$el
    this.registerReference(this.reference)
  },
  render() {
    if (!this.popperVM) {
      return
    }

    const title = this.$slots.title || this.title
    const content = this.$slots.default || this.content

    const reference = this.$refs.reference || this.$slots.reference || this.reference
    const renderReference = getFirstComponentChild(reference)

    if (this.popperVM) {
      const popperStyle = {}
      if (this.popperWidth) {
        popperStyle.width = this.popperWidth + 'px'
      }

      this.popperVM.popperId = this.popperId
      this.popperVM.node = (
        <transition
          onEnter={this.enter}
          onAfterEnter={this.afterEnter}
          onLeave={this.leave}
          onAfterLeave={this.afterLeave}
        >
          <div
            ref="popper"
            v-show={this.showPopper}
            style={popperStyle}
            class={this.popoverCss}
            onMouseenter={() => {
              this.opening = true
            }}
            onMouseleave={() => {
              if (this.trigger === 'hover') {
                this.hide()
              }
            }}
            onMousemove={() => {
              if (this.trigger === 'hover') {
                this.opening = true
              }
            }}
          >
            {
              this.adjustOnChange ? <ResizeAware onResize={this.handleResize} /> : null
            }
            <div class="km-popover__inner">
              {title ? <div class="km-popover__title">{title}</div> : null}
              {content}
            </div>
            {this.arrow ? <div
              x-arrow
              class="km-popover__arrow"
            /> : null}
          </div>
        </transition>
      )
    }

    return renderReference
  },
  methods: {
    updatePosition() {
      this.popperEngine && this.popperEngine.scheduleUpdate()
    },
    handleResize: _.debounce(function ({ width, height }) {
      this.updatePosition()

      this.$emit('resize', { width, height })
    }, 50),
    registerReference(reference) {
      if (reference && !reference.dataset) {
        return
      }

      if (!!reference.dataset.popover === true) {
        return
      }

      reference.dataset.popover = true
      this.reference = reference

      if (reference.nodeType === 1) {
        $(reference).attr({
          tabindex: 0,
          'aria-describedby': this.popperId,
        })

        this.bindTriggerEvents(reference)
      }
    },
    enter(el, done) {
      if (KDevice.noAnim) {
        done()
        return
      }

      raf(() => {
        el.style.opacity = 0
        el.style.transition = 'none'

        raf(() => {
          el.style.opacity = 1
          el.style.transition = `opacity .25s linear`
          $(el).one(transitionEnd, () => {
            done()
          })
        })
      })
    },
    afterEnter(el) {
      el.style.transition = 'none'
    },
    leave(el, done) {
      if (KDevice.noAnim) {
        done()
        return
      }

      raf(() => {
        el.style.transition = 'none'
        // transitionEnd可能存在没有执行done()但是opacity为0的透明蒙层情况，暂时去掉leave动画
        done()
        // raf(() => {
        //   el.style.opacity = 0
        //   el.style.transition = `opacity .15s linear`

        //   $(el).one(transitionEnd, () => {
        //     done()
        //   })
        // })
      })
    },
    afterLeave(el) {
      el.style.transition = 'none'
      this.destroyPopper()
    },
  },
}

const getPopperByRefName = function (refName, component) {
  const popper = component.$refs[refName]
  if (popper) {
    return popper
  }
  const parent = component.$parent
  if (parent) {
    return getPopperByRefName(refName, parent)
  }

  return null
}

const bindReference = (el, binding, vnode) => {
  const popoverRef = binding.value || binding.arg

  // const popper = vnode.context.$refs[popoverRef]
  const popper = getPopperByRefName(popoverRef, vnode.context)
  // console.log('popoverRef: ', popoverRef, popper)
  if (popper) {
    popper.reference = el
    if (popper.references.indexOf(el) === -1) {
      popper.references.push(el)
    }
  }
}

const unbindReference = (el, binding, vnode) => {
  const popoverRef = binding.value || binding.arg

  // const popper = vnode.context.$refs[popoverRef]
  const popper = getPopperByRefName(popoverRef, vnode.context)
  // console.log('popoverRef: ', popoverRef, popper)
  if (popper) {
    popper.references = popper.references.filter(_el => el !== _el)
  }
}

export const directive = {
  name: 'popover',
  bind(el, binding, vnode) {
    bindReference(el, binding, vnode)
    el.__handler__ = () => {
      bindReference(el, binding, vnode)
    }
    $(el).on('mouseenter.popover.active', el.__handler__)
    $(el).on('mousedown.popover.active', el.__handler__)
  },
  inserted(el, binding, vnode) {
    bindReference(el, binding, vnode)
  },
  unbind(el, binding, vnode) {
    unbindReference(el, binding, vnode)
    if (el.__handler__) {
      $(el).off('mouseenter.popover.active', el.__handler__)
      $(el).off('mousedown.popover.active', el.__handler__)
    }
  },
}
